import { type MailRibbonTabId } from "owa-ribbon-ids/lib/mailRibbonId";
import type { ReadOnlyRibbonControlDefProps, RibbonControlDefinitionProps, } from "owa-mail-ribbon-utils";
import { saveDensityOption } from "owa-display-density-option";
import { lazyChangeNoteColor, lazyCreateNewNote, lazyDeleteNote, lazyViewEmailFromNote, } from "owa-notes-store";
import type DisplayDensityMode from "owa-service/lib/contract/DisplayDensityMode";
import type { MenuOpenOnExecuteParameter } from "owa-acui";
import type { OfficeOnlineApp } from "owa-file/lib/types/OfficeOnlineApp";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import type ConversationSortOrder from "owa-service/lib/contract/ConversationSortOrder";
import type { ViewType } from "owa-mail-organization-settings";
import type { MailboxInfo } from "owa-client-types";
import { getUserConfiguration } from "owa-session-store";
import checkAndCreateNewMessage from "owa-mail-message-actions/lib/actions/checkAndCreateNewMessage";
import { getActionSourceFromCommandingViewMode } from "owa-command-ribbon";
import { getRibbonMailboxInfo } from "owa-mail-ribbon-utils/lib/getRibbonMailboxInfo";
import { getSelectedPublicFolderId } from "owa-publicfolder-utils";
import { handleOfficeAppClick } from "owa-mail-new-message-button/lib/utils/getOfficeAppsMenuOptions";
import initializeYammerPublisher from "owa-mail-commands/lib/actions/initializeYammerPublisher";
import { isGlobalFeatureEnabled } from "owa-feature-flags/lib/utils/isFeatureEnabled";
import { isYammerPublisherItemEnabled } from "owa-mail-ribbon-utils/lib/isYammerPublisherItemEnabled";
import { launchCalendarFullCompose } from "owa-calendar-forms-launch-handlers/lib/actions/launchCalendarFullCompose";
import { lazyInitializeConfigForYammer } from "owa-yammer-prebootstrap";
import { lazyMountAndShowFullOptions } from "owa-options-view";
import { lazyOnOpenCustomizer } from "owa-mail-ribbon-customizer";
import { lazySaveReadingPaneOption } from "owa-reading-pane-option";
import { toggleFolderPaneExpansion } from "owa-mail-layout";
import { logUsage } from "owa-analytics";

import onMessagePreviewSelection from "owa-messagelist-layout-option/lib/utils/saveMessagePreviewOption";
import onNewYammerPost from "owa-mail-commands/lib/actions/onNewYammerPost";
import onRestore from "owa-mail-commands/lib/actions/onRestore";
import onUndo from "owa-mail-commands/lib/actions/onUndo";
import { lazySaveConversationsOptionQuick, lazySaveStackedModeOptionsQuick, } from "owa-conversations-option";
import { lazySaveConversationsV2OptionQuick } from "owa-conversationsv2-option/lib/ConversationsV2Option";
import { lazyRestoreAllItems } from "owa-mail-triage-action";
import { getSelectedTableView } from "owa-mail-list-store";
import { lazyLaunchRemindersPopup } from "owa-header-app-notifications-helpers";
import { SearchScenarioId, setSearchHitHighlightingEnabled } from "owa-search-store";
import type { StateChangeSource } from "owa-left-pane-layout/lib/types/StateChangeSource";
import { isAllItemPartsExpanded } from "owa-mail-reading-pane-store-conversation/lib/utils/isAllItemPartsExpanded";
import { lazyExpandCollapseAllItemParts } from "owa-mail-reading-pane-store-conversation";
import toggleLightRP from "owa-light-reading-pane-store/lib/actions/toggleLightRP";
import { lazyManualSyncMail } from "owa-mail-manual-sync";
import { lazyToggleIsAlphabetizeFoldersEnabled } from "owa-folders";
import { setBitAndUpdateAccountScopeUserSettingsAndService, type FolderPaneBitFlagsMasks } from "owa-bit-flags/lib/utils/folderPaneBitFlagsUtil";
import { lazyUpdateScenarioInResizeMode, ResizeHandleScenario } from "owa-resize-handle";
import { lazyShowImportTemplateDialog } from "owa-mail-compose-control-sections/lib/sections";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { logTemplateImportSteps } from "owa-mail-templates-option";
import { setShouldShow as setShouldShowNewFolderDialog } from "owa-mail-newfolder-dialog/lib/actions/setShouldShow";
import { lazyCreateSearchFolder } from "owa-search-folders-option/lib/SearchFoldersOption";
import { setInitialAccountSelectorMailboxInfo } from "owa-account-selector/lib/actions/publicActions";
import { lazyOpenFolderPermissions } from "owa-mail-folder-permissions";

export { onToggleSelectedFolderFavorite } from "./onFavoriteCommandActions";
export { onToggleFocusedInbox } from "./onFocusedInboxCommandActions";

export const onNewEvent = (props: RibbonControlDefinitionProps): void => {
    launchCalendarFullCompose({
        fullComposeEntrySource: "Mail_NewItems_EventButton",
        mailboxInfo: getRibbonMailboxInfo(props.mailboxInfo),
    });
};

export const onToggleFolderPane = (changeSource: StateChangeSource, newState: boolean | undefined): void => {
    toggleFolderPaneExpansion(changeSource, true /*persistOnServer*/, newState);
};

export const onFolderPaneResizeClicked = (): void => {
    lazyUpdateScenarioInResizeMode.importAndExecute(ResizeHandleScenario.FolderPane);
};

export const onNewFolderClicked = (mailboxInfo: MailboxInfo): void => {
    setInitialAccountSelectorMailboxInfo(mailboxInfo);
    setShouldShowNewFolderDialog(true /* shouldShow */);
};

export const onNewSearchFolderClicked = async (mailboxInfo: MailboxInfo): Promise<void> => {
    await lazyCreateSearchFolder.importAndExecute(mailboxInfo);
    await lazyMountAndShowFullOptions.importAndExecute("mail", "searchFolders", undefined /* option */, undefined /* targetWindow */, mailboxInfo);
};

export const onFolderPermissionsClicked = async (folderId: string | undefined, mailboxInfo: MailboxInfo): Promise<void> => {
    if (!folderId) {
        logUsage("TnS_Permissions", mailboxInfo, { MissingFolderId: true }, { isScorecardEvent: true });
        return;
    }
    
    await (await lazyOpenFolderPermissions.import())(folderId, mailboxInfo);
    
    logUsage("TnS_Permissions", mailboxInfo, { entryPoint: "Ribbon" }, { isScorecardEvent: true });
};

export const onNewMessage = (eventTimestamp?: number): void => {
    if (isGlobalFeatureEnabled("tri-exp-scorecard-test")) {
        // Scorecard pipeline test flight — no product behavior
    }
    checkAndCreateNewMessage(getActionSourceFromCommandingViewMode(), null /* targetId */, eventTimestamp);
};

export const onNewPost = (eventTimestamp?: number): void => {
    checkAndCreateNewMessage(getActionSourceFromCommandingViewMode(), getSelectedPublicFolderId(), eventTimestamp);
};

export const onUndoClicked = (): void => {
    onUndo(getActionSourceFromCommandingViewMode());
};

export const onNewEmailSplitButtonExecuted = (parameter: MenuOpenOnExecuteParameter, mailboxInfo: MailboxInfo): void => {
    const isShadowRendering = !!parameter?.isShadowRendering == true;
    if (isShadowRendering) {
        return;
    }
    
    if (isGlobalFeatureEnabled("tri-newYammerPostButton")) {
        lazyInitializeConfigForYammer.importAndExecute();
    }
    
    if (isYammerPublisherItemEnabled()) {
        initializeYammerPublisher();
        logUsage("YammerPublisher_MenuItemSeen", mailboxInfo);
    }
};

export const onNewYammerPostClicked = (): void => {
    onNewYammerPost();
};

export const getOfficeAppClickHandler = (officeApp: OfficeOnlineApp, mailboxInfo: MailboxInfo): void => handleOfficeAppClick(officeApp, mailboxInfo);

export const onRibbonCustomizerClicked = (tabId: MailRibbonTabId, mailboxInfo: MailboxInfo): void => {
    // UIB ribbon customization is not supported
    const effectiveMailboxInfo = isUnifiedInboxMailboxInfo(mailboxInfo)
        ? getGlobalSettingsAccountMailboxInfo()
        : mailboxInfo;
    switch (tabId) {
        case 1:
        case 3:
            lazyOnOpenCustomizer.importAndExecute(tabId, effectiveMailboxInfo);
            break;
        default:
            // If the tabId is not recognized, default to opening the customizer for the Home tab
            lazyOnOpenCustomizer.importAndExecute(1, effectiveMailboxInfo);
            break;
    }
};

export const onNewNoteClicked = (): void => {
    lazyCreateNewNote.importAndExecute("NotesFolder");
};

export const onDeleteNoteClicked = (): void => {
    lazyDeleteNote.importAndExecute("NotesFolder");
};

export const onViewEmailFromNoteClicked = (): void => {
    lazyViewEmailFromNote.importAndExecute("NotesFolder");
};

export const onViewSettingsClicked = (props: ReadOnlyRibbonControlDefProps): void => {
    const mailboxInfo = getRibbonMailboxInfo(props.mailboxInfo);
    lazyMountAndShowFullOptions.importAndExecute("mail", "messageList", undefined /* option */, undefined /* targetWindow */, mailboxInfo);
};

export const onConversationModeClicked = (listType: ReactListViewType): void => {
    lazySaveConversationsOptionQuick.importAndExecute(listType);
};

export const onReadingPaneViewTypeClicked = (readingPaneViewType: ViewType): void => {
    lazySaveConversationsV2OptionQuick.importAndExecute(undefined, readingPaneViewType);
};

export const onMessageListViewTypeClicked = (messageListViewType: ViewType): void => {
    lazySaveConversationsV2OptionQuick.importAndExecute(messageListViewType, undefined);
};

export const onMessagePreviewClicked = (selected: number): void => {
    onMessagePreviewSelection(selected);
};

export const onReadingPanePositionClicked = (readingPanePosition: number): void => {
    lazySaveReadingPaneOption.importAndExecute(readingPanePosition);
};

export const onReadingPaneResizeClicked = (): void => {
    lazyUpdateScenarioInResizeMode.importAndExecute(ResizeHandleScenario.ReadingPane);
};

export const onReadingPaneOrderClicked = (order: ConversationSortOrder): void => {
    lazySaveStackedModeOptionsQuick.importAndExecute(order);
};

export const onDensityClicked = (newDensity: DisplayDensityMode): void => {
    const existingDensity = getUserConfiguration().UserOptions?.DisplayDensityMode;
    
    if (newDensity !== existingDensity) {
        logUsage("DisplayDensityRibbon", getGlobalSettingsAccountMailboxInfo(), {
            existingDensityMode: existingDensity ?? "NotSet",
            newDensityMode: newDensity,
        });
    }
    
    saveDensityOption(newDensity);
};

export const onChangeNoteColorClicked = (newColor: string): void => {
    lazyChangeNoteColor.importAndExecute(newColor, "NotesFolder");
};

export const onRestoreClicked = (): void => {
    onRestore(getActionSourceFromCommandingViewMode());
};

export const onRestoreAllClicked = (): void => {
    lazyRestoreAllItems.importAndExecute(getSelectedTableView(), getActionSourceFromCommandingViewMode());
};

export const onRemindersClicked = (mailboxInfo: MailboxInfo): void => {
    logUsage("RemindersButtonClicked_Mail", mailboxInfo);
    lazyLaunchRemindersPopup.importAndExecute(true /*isManuallyOpen*/);
};

export const onExpandCollapseConversationClicked = (itemId: string, mailboxInfo: MailboxInfo): void => {
    const allItemsExpanded = isAllItemPartsExpanded(itemId);
    lazyExpandCollapseAllItemParts.importAndExecute(itemId, !allItemsExpanded /*shouldExpand*/, false /*isFromShortcut*/, mailboxInfo);
};

export const onSyncMailboxClicked = (mailboxInfo: MailboxInfo): void => {
    logUsage("SyncButtonClicked", mailboxInfo, {
        source: "Ribbon",
    });
    lazyManualSyncMail.importAndExecute(getSelectedTableView());
};

export const onWorkOfflineClicked = (mailboxInfo: MailboxInfo): void => {
    lazyMountAndShowFullOptions.importAndExecute("general", "offline-subcategory", "offlineOption", undefined /* targetWindow */, mailboxInfo);
};

export const onSearchHighlightingControlClicked = (mailboxInfo: MailboxInfo, isEnabled: boolean): void => {
    logUsage("Search_HighlightControlClicked", mailboxInfo, { isEnabled });
    setSearchHitHighlightingEnabled(SearchScenarioId.Mail, isEnabled);
};

export const onAlphabetizeFoldersClicked = async (mailboxInfo: MailboxInfo, isEnabled: boolean): Promise<void> => {
    await setBitAndUpdateAccountScopeUserSettingsAndService(isEnabled, 1, mailboxInfo);
    lazyToggleIsAlphabetizeFoldersEnabled.importAndExecute(mailboxInfo, isEnabled);
    if (isEnabled) {
        logUsage("FP_MoveFolderPos_AtoZSelected", mailboxInfo, { source: "ribbon" });
    }
    else {
        logUsage("FP_MoveFolderPos_CustomOrderSelected", mailboxInfo, { source: "ribbon" });
    }
};

export const onLightRPClicked = (): void => {
    toggleLightRP();
};

export const onImportTemplateClicked = (): void => {
    logTemplateImportSteps("NewMailFromTemplate");
    lazyShowImportTemplateDialog.import().then(showImportTemplateDialog => {
        showImportTemplateDialog();
    });
};
